// Roles in a shared account and what each may do in the app
// (audits/team-access-plan-2026-10/PLAN.md §3). The server decides for real
// (eventlive-cdk/teams/src/policy.ts, fail-closed); this only hides what a
// role can't do, so members don't meet buttons that would be refused.
//
// The labels and descriptions are translated when the module loads: index.js
// sets the language before the app is required (localization/README.md).

import { t } from "@/localization";

export const ROLES = ["admin", "editor", "moderator"];
export const ROLE_RANK = { moderator: 1, editor: 2, admin: 3, holder: 4 };
export const isRole = (value) => ROLES.includes(value);

export const ROLE_LABELS = {
  admin: t("Admin"),
  editor: t("Editor"),
  moderator: t("Moderator"),
  holder: t("Account holder"),
};

// For the invite form and the role picker, by what the invite covers.
export const ROLE_DESCRIPTIONS = {
  account: {
    admin: t(
      "Everything you can do, including inviting people, except buying credits or plans and deleting the account."
    ),
    editor: t(
      "Creates and runs events: details, going live, guests and video. Can't delete, unlock or sell tickets."
    ),
    moderator: t(
      "Looks after guestbooks and chats. Sees the events and their links, nothing else."
    ),
  },
  event: {
    admin: t(
      "Everything you can do with this event, including deleting it and inviting people to it."
    ),
    editor: t(
      "Runs this event: details, going live, guests and video. Can't delete it, unlock it or sell tickets."
    ),
    moderator: t(
      "Looks after this event's guestbook or chat. Sees the event and its link, nothing else."
    ),
  },
};

// The same, told to the person invited (the invite page).
export const ROLE_INVITEE_DESCRIPTIONS = {
  account: {
    admin: t(
      "You can do everything the account holder can, except buy credits or plans and delete the account."
    ),
    editor: t(
      "You can create and run events: details, going live, guests and video."
    ),
    moderator: t("You can look after the events' guestbooks and chats."),
  },
  event: {
    admin: t(
      "You can do everything with this event, including inviting others to it."
    ),
    editor: t("You can run this event: details, going live, guests and video."),
    moderator: t("You can look after this event's guestbook or chat."),
  },
};

export const higherRole = (a, b) => {
  if (!isRole(a)) return isRole(b) ? b : null;
  if (!isRole(b)) return a;
  return ROLE_RANK[b] > ROLE_RANK[a] ? b : a;
};

// The least role each action needs. "holder": only the account holder.
export const PERMISSIONS = {
  viewEvent: "moderator", // the event, its status, the viewer link
  moderate: "moderator", // delete guestbook and chat messages
  goLive: "editor", // go live, test, stream key, multi-camera
  editEvent: "editor", // title, date, cover, branding, links, privacy, embed
  guests: "editor", // add and import guests, send invitations
  video: "editor", // upload, trim, downloads, guestbook PDF
  analytics: "editor",
  createEvent: "editor", // whole-account members only
  removeGuest: "admin",
  deleteRecording: "admin", // delete or replace recordings
  deleteEvent: "admin",
  addRoom: "admin", // costs credits
  spendCredits: "admin", // unlock with the account's credits
  sellTickets: "admin",
  manageTeam: "admin", // invite, change and remove people
  fundraising: "admin", // evt.live checks the role too (x-el-owner)
  pinMessage: "moderator", // a pinned guestbook or chat message
  buy: "holder", // credits, plans, unlock and storage purchases
};

// Need that role on the whole account, not just on an event: creating events,
// and removing guests (the server checks deleteGuestById on the account, as a
// guest's id names no event).
const ACCOUNT_ONLY = new Set(["createEvent", "removeGuest"]);

const text = (value) =>
  typeof value === "string" && value.trim() ? value.trim() : null;

// What to call a shared account: its company, else its holder's name.
export const accountNameOf = (row) =>
  text(row?.company_name) || text(row?.owner_name) || row?.owner || "";

// sharedWithMe rows grouped by owner, one entry per account:
// { owner, name, logo_path, accountRole, events: [{ slug, title, role }],
//   eventRoles: { slug: role }, slugs, role, scope }.
// `role` is the account role, else the highest event role; `scope` says
// whether the whole account is shared ("account") or only events ("event").
export function groupSharedAccess(rows) {
  const byOwner = new Map();
  for (const row of Array.isArray(rows) ? rows : []) {
    if (!row || !row.owner || !isRole(row.role)) continue;
    let entry = byOwner.get(row.owner);
    if (!entry) {
      entry = {
        owner: row.owner,
        name: accountNameOf(row),
        owner_name: text(row.owner_name),
        company_name: text(row.company_name),
        logo_path: text(row.logo_path),
        app: row.app || null,
        accountRole: null,
        events: [],
        eventRoles: {},
      };
      byOwner.set(row.owner, entry);
    }
    if (row.slug) {
      entry.eventRoles[row.slug] = higherRole(
        entry.eventRoles[row.slug],
        row.role
      );
      if (!entry.events.some((e) => e.slug === row.slug)) {
        entry.events.push({
          slug: row.slug,
          title: text(row.event_title),
          role: row.role,
        });
      }
    } else {
      entry.accountRole = higherRole(entry.accountRole, row.role);
    }
  }
  return [...byOwner.values()].map((entry) => {
    const eventBest = Object.values(entry.eventRoles).reduce(
      (best, role) => higherRole(best, role),
      null
    );
    return {
      ...entry,
      slugs: entry.events.map((e) => e.slug),
      role: entry.accountRole || eventBest,
      scope: entry.accountRole ? "account" : "event",
    };
  });
}

export const accessForOwner = (rows, owner) =>
  owner ? groupSharedAccess(rows).find((a) => a.owner === owner) || null : null;

// Your role on one event of the shared account: the higher of your account
// role and your role on the event (rooms count as their main event). With only
// a slug that isn't one of your events (a room opened by its own slug), the
// best of your event roles stands in; the server checks the real one.
export function roleForEvent(access, event) {
  if (!access) return "holder";
  const slug =
    (event && (event.room_of || event.slug)) ||
    (typeof event === "string" ? event : null);
  const known = slug ? access.eventRoles?.[slug] : null;
  if (known || access.accountRole) {
    return higherRole(access.accountRole, known);
  }
  return access.role || null;
}

// May the person do `permission` here? `access` is the current shared
// account's entry (groupSharedAccess), or null in your own account (the
// holder may do everything). Without an event it's about the whole account,
// so only a whole-account role counts (an event admin can't run the account's
// team, for example).
export function can(access, permission, event) {
  const need = PERMISSIONS[permission];
  if (!need) return false; // unknown permissions are refused, like the server
  if (!access) return true;
  if (need === "holder") return false;
  const role =
    event && !ACCOUNT_ONLY.has(permission)
      ? roleForEvent(access, event)
      : access.accountRole;
  return !!role && ROLE_RANK[role] >= ROLE_RANK[need];
}
